import { zodResolver } from '@hookform/resolvers/zod'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import { toCents } from '@ankor-io/common/domain/pay/currency'
import { type CharterSummary, type CreateCharterRequest } from '@ankor-io/common/types/charter'
import { currencies } from '@ankor-io/common/util/currencies'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { ErrorMessage, Field, Label } from '@/ui-components/Fieldset'
import { Heading } from '@/ui-components/Heading'
import { Input } from '@/ui-components/Input'
import { Select } from '@/ui-components/Select'

// Charter fee, apa budget, vat rate are strings because the form schema does not coerce the values to numbers
// otherwise if we explicitly set the type to number in the form register itself, it won't form it's own non-user friendly error message
const formSchema = z.object({
  charterName: z.string().min(2, {
    message: 'Charter name is required.',
  }),
  charterFee: z
    .string()
    .min(1, {
      message: 'Charter fee is required.',
    })
    .refine(
      (val) => {
        const num = parseFloat(val)
        return !isNaN(num) && num >= 0 && num <= 9999999.99
      },
      {
        message: 'Amount must be between 0 and 9,999,999.99',
      },
    ),
  apaBudget: z
    .string()
    .min(1, {
      message: 'APA budget is required.',
    })
    .refine(
      (val) => {
        const num = parseFloat(val)
        return !isNaN(num) && num >= 0 && num <= 9999999.99
      },
      {
        message: 'Amount must be between 0 and 9,999,999.99',
      },
    ),
  primaryCurrency: z.string().min(1, {
    message: 'Currency is required.',
  }),
  vatRate: z.preprocess(
    (val) => (val === '' || val === null || typeof val === 'undefined' ? undefined : Number(val)),
    z
      .number()
      .min(1, {
        message: 'VAT amount must be greater than 0',
      })
      .max(100, {
        message: 'VAT amount cannot be greater than 100',
      }),
  ),
  embarkationDate: z.string().min(1, {
    message: 'Embarkation date is required.',
  }),
  disembarkationDate: z.string().min(1, {
    message: 'Disembarkation date is required.',
  }),
  placeOfDelivery: z.string().min(2, {
    message: 'Place of delivery is required.',
  }),
  placeOfRedelivery: z.string().min(2, {
    message: 'Place of redelivery is required.',
  }),
  chartererName: z.string().min(2, {
    message: 'Charterer name is required.',
  }),
  chartererEmail: z.string().email({
    message: 'Invalid email address.',
  }),
  countryCode: z
    .string()
    .min(2, {
      message: 'Country code is too short.',
    })
    .max(4, {
      message: 'Country code is too long',
    }),
  phoneNumber: z
    .string()
    .min(8, { message: 'Phone number is too short' })
    .max(14, { message: 'Phone number is too long' }),
})

const CharterNewPage: React.FC = () => {
  const navigate = useNavigate()
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const [isCreating, setIsCreating] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      charterName: '',
      charterFee: '',
      apaBudget: '',
      primaryCurrency: 'USD',
      vatRate: undefined,
      embarkationDate: '',
      disembarkationDate: '',
      placeOfDelivery: '',
      placeOfRedelivery: '',
      chartererName: '',
      chartererEmail: '',
      countryCode: '+1',
      phoneNumber: '',
    },
  })

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    const submit = async () => {
      setIsCreating(true)

      try {
        // convert embarkationDate and disembarkationDate to ISO-8601 DateTime
        const payload: CreateCharterRequest = {
          name: values.charterName,
          startDate: new Date(values.embarkationDate).toISOString(),
          endDate: new Date(values.disembarkationDate).toISOString(),
          delivery: values.placeOfDelivery,
          reDelivery: values.placeOfRedelivery,
          charterFeeCents: toCents(Number(values.charterFee)),
          apaCents: toCents(Number(values.apaBudget)),
          vat: values.vatRate?.toString(),
          contactFullName: values.chartererName,
          contactEmail: values.chartererEmail,
          contactMobileNumber: values.phoneNumber,
          contactMobileCountryCode: values.countryCode,
          currencyCode: values.primaryCurrency,
        }

        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/charter`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            method: 'POST',
            body: JSON.stringify(payload),
          },
        )

        if (!res.ok) {
          throw new Error('Failed to create charter.')
        }

        const data: CharterSummary = await res.json()
        form.reset()

        // Navigate to the new charter detail page
        navigate(`/charters/${data.id}`)
      } catch (error) {
        console.error('Error creating charter:', error)
        throw new Error('Failed to create charter.')
      } finally {
        setIsCreating(false)
      }
    }

    toast.promise(submit, {
      loading: 'Creating charter...',
      success: 'Charter created successfully.',
      error: 'Failed to create charter.',
    })
  }

  return (
    <>
      <Button
        plain
        className="mb-2"
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        <ArrowLeft data-slot="icon" />
        Back
      </Button>

      <div className="space-y-6">
        <div className="mb-6">
          <Heading level={3}>Create New Charter</Heading>
          <p className="text-gray-600 dark:text-gray-400">
            Set up a new charter with all the necessary details and documentation.
          </p>
        </div>

        <form
          role="form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-6"
        >
          {/* Charter Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100">Charter Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
              <Field>
                <Label>Charter Name</Label>
                <Input
                  placeholder="Charter Name"
                  {...form.register('charterName')}
                />
                {form.formState.errors.charterName && (
                  <ErrorMessage>{form.formState.errors.charterName.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Charter Fee</Label>
                <Input
                  type="number"
                  placeholder="Charter Fee"
                  {...form.register('charterFee')}
                />
                {form.formState.errors.charterFee && (
                  <ErrorMessage>{form.formState.errors.charterFee.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Contracted APA Amount</Label>
                <Input
                  type="number"
                  placeholder="APA Budget"
                  {...form.register('apaBudget')}
                />
                {form.formState.errors.apaBudget && (
                  <ErrorMessage>{form.formState.errors.apaBudget.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Primary Charter Currency</Label>
                <Select {...form.register('primaryCurrency')}>
                  {currencies.map((currency) => (
                    <option
                      key={currency.code}
                      value={currency.code}
                    >
                      {currency.code}
                    </option>
                  ))}
                </Select>
                {form.formState.errors.primaryCurrency && (
                  <ErrorMessage>{form.formState.errors.primaryCurrency.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>VAT Rate (%)</Label>
                <Input
                  type="number"
                  placeholder="VAT Rate"
                  {...form.register('vatRate')}
                />
                {form.formState.errors.vatRate && <ErrorMessage>{form.formState.errors.vatRate.message}</ErrorMessage>}
              </Field>
            </div>
          </div>

          {/* Charter Dates & Locations Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100">Charter Dates & Locations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
              <Field>
                <Label>Embarkation Date</Label>
                <Input
                  type="date"
                  {...form.register('embarkationDate')}
                />
                {form.formState.errors.embarkationDate && (
                  <ErrorMessage>{form.formState.errors.embarkationDate.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Disembarkation Date</Label>
                <Input
                  type="date"
                  {...form.register('disembarkationDate')}
                />
                {form.formState.errors.disembarkationDate && (
                  <ErrorMessage>{form.formState.errors.disembarkationDate.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Place of Delivery</Label>
                <Input
                  placeholder="Place of Delivery"
                  {...form.register('placeOfDelivery')}
                />
                {form.formState.errors.placeOfDelivery && (
                  <ErrorMessage>{form.formState.errors.placeOfDelivery.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Place of Re-delivery</Label>
                <Input
                  placeholder="Place of Re-delivery"
                  {...form.register('placeOfRedelivery')}
                />
                {form.formState.errors.placeOfRedelivery && (
                  <ErrorMessage>{form.formState.errors.placeOfRedelivery.message}</ErrorMessage>
                )}
              </Field>
            </div>
          </div>

          {/* Primary Charterer Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-100">Primary Charterer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border rounded-lg p-4 border-gray-50 dark:border-gray-800">
              <Field>
                <Label>Full Name</Label>
                <Input
                  placeholder="Full Name"
                  {...form.register('chartererName')}
                />
                {form.formState.errors.chartererName && (
                  <ErrorMessage>{form.formState.errors.chartererName.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="Email"
                  {...form.register('chartererEmail')}
                />
                {form.formState.errors.chartererEmail && (
                  <ErrorMessage>{form.formState.errors.chartererEmail.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Country Code</Label>
                <Input
                  type="text"
                  placeholder="Country Code"
                  {...form.register('countryCode')}
                />
                {form.formState.errors.countryCode && (
                  <ErrorMessage>{form.formState.errors.countryCode.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Phone Number</Label>
                <Input
                  placeholder="Phone Number"
                  {...form.register('phoneNumber')}
                />
                {form.formState.errors.phoneNumber && (
                  <ErrorMessage>{form.formState.errors.phoneNumber.message}</ErrorMessage>
                )}
              </Field>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-6">
            <Button
              outline
              to="/charters"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={isCreating}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Charter
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

export default CharterNewPage
